// src/utils/googleApiHelpers.js

const loadGapiScript = () => {
    return new Promise((resolve, reject) => {
        if (window.gapi) {
            resolve(window.gapi);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.onload = () => {
            window.gapi.load('client', {
                callback: () => resolve(window.gapi),
                onerror: reject,
            });
        };
        script.onerror = () => {
            reject(new Error('Failed to load GAPI script.'));
        };
        document.body.appendChild(script);
    });
};

const formatDataForSheet = (data, headers) => {
    const headerRow = Object.values(headers);
    const dataRows = data.map(row =>
        Object.keys(headers).map(key => {
            const value = row[key];
            if (value && typeof value.toDate === 'function') {
                return value.toDate().toLocaleString('es-AR');
            }
            if (Array.isArray(value)) {
                return value.map(item => {
                    // Activos de pases: "GD30: 10000 (usado 4000)". Garantías: "GD30: 10000".
                    if (item && item.cantidad !== undefined) {
                        const usado = item.cantidadUsada ? ` (usado ${item.cantidadUsada})` : '';
                        return `${item.especie || ''}: ${item.cantidad}${usado}`;
                    }
                    return `${item.especie || ''}: ${item.vn || ''}`;
                }).join('; ');
            }
            if (typeof value === 'boolean') {
                return value ? 'Sí' : 'No';
            }
            return value != null ? value : '';
        })
    );
    return [headerRow, ...dataRows];
};

// --- ✅ CAMBIO: La función ahora recibe los indicadores ---
export const exportToGoogleSheets = async (
    cauciones,
    operaciones,
    senebiOperations,
    instrucciones,
    caucionIndicators,
    senebiFeeTotals,
    apiKey,
    clientId,
    sharedDriveId,
    pases = []
) => {
    const gapi = await new Promise((resolve, reject) => {
        loadGapiScript().then(gapi => {
            gapi.client.init({
                apiKey: apiKey,
                discoveryDocs: [
                    "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
                    "https://www.googleapis.com/discovery/v1/apis/sheets/v4/rest"
                ],
            }).then(() => {
                const tokenClient = window.google.accounts.oauth2.initTokenClient({
                    client_id: clientId,
                    scope: 'https://www.googleapis.com/auth/drive.file',
                    callback: (tokenResponse) => {
                        if (tokenResponse && tokenResponse.access_token) {
                            gapi.client.setToken(tokenResponse);
                            resolve(gapi);
                        } else {
                            reject(new Error('Failed to get access token.'));
                        }
                    },
                    error_callback: reject,
                });
                tokenClient.requestAccessToken();
            });
        });
    });

    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;
    const fileName = `${formattedDate} - Planilla de Operaciones Axon Capital`;

    const fileMetadata = {
        name: fileName,
        mimeType: 'application/vnd.google-apps.spreadsheet',
        parents: [sharedDriveId]
    };

    const driveResponse = await gapi.client.drive.files.create({
        resource: fileMetadata,
        supportsAllDrives: true,
    });

    const spreadsheetId = driveResponse.result.id;

    const caucionesHeaders = { createdAt: 'Fecha', operacion: 'Operación', cliente: 'Cliente', monto: 'Monto', montoOperado: 'Monto Operado', dias: 'Días', tasa: 'Tasa', moneda: 'Moneda', comercial: 'Comercial', operador: 'Operador', estado: 'Estado', garantias: 'Garantías', comentarioTrader: 'Com. Trader', comentarioComercial: 'Com. Comercial' };
    const pasesHeaders = { createdAt: 'Fecha', operacion: 'Operación', nroComitente: 'Nro Comitente', cliente: 'Cliente', monto: 'Monto', montoOperado: 'Monto Operado', fechaVencimiento: 'Vencimiento', tasa: 'Tasa', tasaOperada: 'Tasa Operada', moneda: 'Moneda', mercado: 'Mercado', comercial: 'Comercial', operador: 'Operador', estado: 'Estado', activos: 'Activos', comentarioTrader: 'Com. Trader', comentarioComercial: 'Com. Comercial' };
    const operacionesHeaders = { createdAt: 'Fecha', operacion: 'Operación', especie: 'Especie', plazo: 'Plazo', cliente: 'Cliente', cantidad: 'Cantidad', precio: 'Precio', monto: 'Monto', moneda: 'Moneda', comercial: 'Comercial', trader: 'Trader', estado: 'Estado', comentario: 'Com. Trader', comentarioComercial: 'Com. Comercial' };
    const senebiHeaders = { fecha: 'Fecha', operacion: 'Operación', especie: 'Especie', cantidad: 'Cantidad', precio: 'Precio', nroComitente: 'Nro Comitente', cliente: 'Cliente', precioCarteraPropia: 'Precio CP', fee: 'Fee', moneda: 'Moneda', cargada: 'Cargada', comentario: 'Comentario', operador: 'Operador' };
    const instruccionesHeaders = { createdAt: 'Fecha de Carga', tipo: 'Tipo', viaEgreso: 'Vía Egreso', nroComitente: 'Nro Comitente', cliente: 'Cliente', monto: 'Monto', esRescateTotal: 'Rescate Total', cantidad: 'Cantidad', especie: 'Especie', titulos: 'Títulos', fci: 'FCI', contraparte: 'Contraparte', moneda: 'Moneda', comercial: 'Comercial', middleOffice: 'Middle Office', estado: 'Estado', comentarioComercial: 'Com. Comercial', comentarioMiddleOffice: 'Com. Middle Office' };

    const caucionesData = formatDataForSheet(cauciones, caucionesHeaders);
    const pasesData = formatDataForSheet(pases || [], pasesHeaders);
    const operacionesData = formatDataForSheet(operaciones, operacionesHeaders);
    const senebiData = formatDataForSheet(senebiOperations, senebiHeaders);
    const instruccionesData = formatDataForSheet(instrucciones || [], instruccionesHeaders);

    // --- ✅ CAMBIO: Se preparan los datos de los indicadores para la hoja ---
    const caucionIndicatorRows = [
        ["Total Colocadora", caucionIndicators.totalColocadora],
        ["TNA Colocadora", `${(caucionIndicators.tnaColocadora * 100).toFixed(2)}%`],
        [], // Fila en blanco
        ["Total Tomadora", caucionIndicators.totalTomadora],
        ["TNA Tomadora", `${(caucionIndicators.tnaTomadora * 100).toFixed(2)}%`]
    ];
    
    const senebiFeeRows = [
        ["Fee Total Pesos", senebiFeeTotals.Pesos],
        ["Fee Total USD Mep", senebiFeeTotals["USD Mep"]],
        ["Fee Total USD CCL", senebiFeeTotals["USD CCL"]]
    ];

    const sheetsRequests = [
        { updateSheetProperties: { properties: { sheetId: 0, title: 'Cauciones' }, fields: 'title' } },
        { addSheet: { properties: { title: 'Pases' } } },
        { addSheet: { properties: { title: 'Operaciones' } } },
        { addSheet: { properties: { title: 'SENEBI' } } },
        { addSheet: { properties: { title: 'Instrucciones' } } }
    ];

    await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId: spreadsheetId,
        resource: { requests: sheetsRequests },
    });

    // --- ✅ CAMBIO: Se envían todos los datos, incluyendo indicadores, en un solo batch ---
    const dataToWrite = [];

    // Añadir indicadores de cauciones
    dataToWrite.push({
        range: 'Cauciones!A1',
        values: caucionIndicatorRows
    });
    // Añadir tabla de cauciones (dejando espacio para los indicadores)
    dataToWrite.push({
        range: `Cauciones!A${caucionIndicatorRows.length + 3}`, // +3 para dejar dos filas en blanco
        values: caucionesData
    });

    // Añadir tabla de pases
    dataToWrite.push({
        range: 'Pases!A1',
        values: pasesData
    });

    // Añadir tabla de operaciones
    dataToWrite.push({
        range: 'Operaciones!A1',
        values: operacionesData
    });

    // Añadir fees de SENEBI
    dataToWrite.push({
        range: 'SENEBI!A1',
        values: senebiFeeRows
    });
    // Añadir tabla de SENEBI (dejando espacio para los fees)
    dataToWrite.push({
        range: `SENEBI!A${senebiFeeRows.length + 3}`, // +3 para dejar dos filas en blanco
        values: senebiData
    });

    // Añadir tabla de Instrucciones
    dataToWrite.push({
        range: 'Instrucciones!A1',
        values: instruccionesData
    });

    await gapi.client.sheets.spreadsheets.values.batchUpdate({
        spreadsheetId: spreadsheetId,
        resource: {
            valueInputOption: 'USER_ENTERED',
            data: dataToWrite
        }
    });

    return `https://docs.google.com/spreadsheets/d/${spreadsheetId}`;
};